// planificationSession.js — Phase 2A foundations (Iter95).
//
// Isolated, in-memory session state for the Planification workspace
// (spec §6 + §9): one FeatureCollection + one undo/redo History, held in
// React state local to whichever component mounts this hook. Nothing here
// touches a server, a Mongo collection, or any SOS/alert/agency data —
// by construction it CANNOT, since it never imports those modules or
// makes a network call.
//
// Not yet mounted inside PlanificationTab in Phase 2A (see Iter95 report)
// — the interface is prepared and unit-tested so Phase 2B's drawing tools
// can wire it to Terra Draw's `finish` events without touching this file's
// shape. Mutating the FeatureCollection is always immutable (every op
// below delegates to planificationGeo's immutable collection helpers),
// and changing the active basemap style / zoom / pan / fullscreen never
// touches this state — it lives entirely outside the MapLibre instance.
import { useCallback, useState } from "react";
import {
  createEmptyFeatureCollection,
  addFeatureToCollection,
  updateFeatureInCollection,
  updateFeatureGeometryInCollection,
  removeFeatureFromCollection,
  replaceFeatureInCollection,
  createFeature,
} from "./planificationGeo";
import { createHistory, recordAction, undo as undoHistory, redo as redoHistory, canUndo, canRedo, ACTION_TYPES } from "./planificationHistory";

export const usePlanificationSession = () => {
  const [featureCollection, setFeatureCollection] = useState(createEmptyFeatureCollection);
  const [history, setHistory] = useState(createHistory);

  /** Creates + inserts a new Feature. Historises it as a single CREATE action. */
  const createAndAddFeature = useCallback((opts) => {
    const feature = createFeature(opts);
    setFeatureCollection((fc) => addFeatureToCollection(fc, feature));
    setHistory((h) => recordAction(h, { type: ACTION_TYPES.CREATE, featureId: feature.id, before: null, after: feature }));
    return feature;
  }, []);

  /**
   * Applies a property update as ONE completed action — call this once
   * per commit (e.g. once a Terra Draw `finish` fires), never per
   * intermediate drag frame; see planificationHistory.js's module doc.
   *
   * `setHistory` is called as a SIBLING top-level statement, never
   * nested inside the `setFeatureCollection` call — React 18 StrictMode
   * deliberately double-invokes a functional state updater to surface
   * side effects; nesting `setHistory(...)` inside one (as an earlier
   * version of this file did) meant that embedded call legitimately
   * fired twice per commit, recording two history entries for one
   * action (confirmed via `window.__plan_simulate_edit` QA, Iter97).
   */
  const updateFeature = useCallback((id, propertyUpdates, { fullCollection } = {}) => {
    const source = fullCollection || featureCollection;
    const before = source.features.find((f) => f.id === id) || null;
    const next = updateFeatureInCollection(source, id, propertyUpdates);
    const after = next.features.find((f) => f.id === id) || null;
    setFeatureCollection(next);
    if (before && after) {
      setHistory((h) => recordAction(h, { type: ACTION_TYPES.UPDATE, featureId: id, before, after }));
    }
  }, [featureCollection]);

  /**
   * Applies a GEOMETRY update (Terra Draw vertex move/add/delete commit)
   * as ONE completed action — `id` and all other properties untouched.
   * Same StrictMode-safe sibling-call pattern as `updateFeature` above.
   */
  const updateFeatureGeometry = useCallback((id, newGeometry, { fullCollection } = {}) => {
    const source = fullCollection || featureCollection;
    const before = source.features.find((f) => f.id === id) || null;
    const next = updateFeatureGeometryInCollection(source, id, newGeometry);
    const after = next.features.find((f) => f.id === id) || null;
    setFeatureCollection(next);
    if (before && after) {
      setHistory((h) => recordAction(h, { type: ACTION_TYPES.UPDATE, featureId: id, before, after }));
    }
  }, [featureCollection]);

  const removeFeature = useCallback((id) => {
    const before = featureCollection.features.find((f) => f.id === id) || null;
    const next = removeFeatureFromCollection(featureCollection, id);
    setFeatureCollection(next);
    if (before) {
      setHistory((h) => recordAction(h, { type: ACTION_TYPES.DELETE, featureId: id, before, after: null }));
    }
  }, [featureCollection]);

  /**
   * "Effacer tous les dessins" (spec §8.5) — ONE logical action, not N
   * individual deletes: `before` is the FULL features array (so Undo
   * restores every object in a single step), `featureId` is null since
   * this targets the whole collection, not one Feature.
   */
  const clearAllFeatures = useCallback(() => {
    if (!featureCollection.features.length) return; // nothing to clear — no-op, no history entry
    setHistory((h) => recordAction(h, { type: ACTION_TYPES.CLEAR_ALL, featureId: null, before: featureCollection.features, after: [] }));
    setFeatureCollection({ ...featureCollection, features: [] });
  }, [featureCollection]);

  /**
   * Returns the applied `{action, applied: "before"|"after"}` (or `null`
   * if the undo/redo stack was empty) SYNCHRONOUSLY — Phase 2C's caller
   * (PlanificationTab.jsx) needs this return value to mirror the change
   * into the live Terra Draw instance (spec §6.6 "Synchroniser Terra
   * Draw"), which this module deliberately knows nothing about. Reads
   * `history` from the closure (current render's value) rather than an
   * `setHistory` updater callback, specifically so this return path works.
   */
  const undo = useCallback(() => {
    const { history: nextHistory, action } = undoHistory(history);
    if (!action) return null;
    setHistory(nextHistory);
    setFeatureCollection((fc) => {
      if (action.type === ACTION_TYPES.CLEAR_ALL) return { ...fc, features: action.before };
      if (action.type === ACTION_TYPES.CREATE) return removeFeatureFromCollection(fc, action.featureId);
      if (action.type === ACTION_TYPES.DELETE) return addFeatureToCollection(fc, action.before);
      return replaceFeatureInCollection(fc, action.before); // UPDATE: restore the full pre-action snapshot
    });
    return { action, applied: "before" };
  }, [history]);

  const redo = useCallback(() => {
    const { history: nextHistory, action } = redoHistory(history);
    if (!action) return null;
    setHistory(nextHistory);
    setFeatureCollection((fc) => {
      if (action.type === ACTION_TYPES.CLEAR_ALL) return { ...fc, features: [] };
      if (action.type === ACTION_TYPES.CREATE) return addFeatureToCollection(fc, action.after);
      if (action.type === ACTION_TYPES.DELETE) return removeFeatureFromCollection(fc, action.featureId);
      return replaceFeatureInCollection(fc, action.after);
    });
    return { action, applied: "after" };
  }, [history]);

  /**
   * Phase 3B — replaces the WHOLE FeatureCollection with the objects
   * loaded from a persisted project version (or an empty project) and
   * resets history: loading a different project is a fresh start, not
   * an undoable action relative to whatever was on-screen before, so
   * carrying over the previous project's undo/redo stack would be
   * actively confusing (e.g. "Undo" appearing to resurrect objects
   * that belong to the PREVIOUS project, not this one).
   */
  const loadFeatureCollection = useCallback((features) => {
    setFeatureCollection({ ...createEmptyFeatureCollection(), features: features || [] });
    setHistory(createHistory());
  }, []);

  return {
    featureCollection,
    createFeature: createAndAddFeature,
    updateFeature,
    updateFeatureGeometry,
    removeFeature,
    clearAllFeatures,
    loadFeatureCollection,
    undo,
    redo,
    canUndo: canUndo(history),
    canRedo: canRedo(history),
  };
};
